import React, { useEffect, useState } from 'react';
import { REPORT_SECTIONS, reportCsv } from '../../analytics/report';
import './AdminDashboard.css';

const number = (value) => new Intl.NumberFormat().format(value || 0);

async function readJson(response) {
  if (!response.headers.get('content-type')?.includes('application/json')) throw new Error('Admin API is unavailable in npm start. Use vercel dev or a Vercel deployment.');
  const body = await response.json();
  if (!response.ok) throw new Error(body.error || 'Request failed');
  return body;
}

function downloadReport(report, days) {
  const blob = new Blob([reportCsv(report)], { type: 'text/csv;charset=utf-8' });
  const url = URL.createObjectURL(blob);
  const link = document.createElement('a');
  link.href = url;
  link.download = `portfolio-analytics-${days}-days.csv`;
  document.body.appendChild(link);
  link.click();
  link.remove();
  URL.revokeObjectURL(url);
}

const Breakdown = ({ title, rows, field, metric = 'pageviews' }) => (
  <section className="admin-panel">
    <h2>{title}</h2>
    {rows.length ? <div className="admin-table-scroll"><table><thead><tr><th scope="col">{title}</th><th scope="col">{metric === 'count' ? 'Events' : 'Views'}</th>{metric !== 'count' && <th scope="col">Visitors</th>}</tr></thead><tbody>{rows.map((row, index) => <tr key={`${row[field]}-${index}`}><td>{row[field] || 'Direct / unknown'}</td><td>{number(row[metric])}</td>{metric !== 'count' && <td>{number(row.visitors)}</td>}</tr>)}</tbody></table></div> : <p className="admin-empty">No data in this period.</p>}
  </section>
);

const AdminDashboard = () => {
  const [authenticated, setAuthenticated] = useState(null);
  const [password, setPassword] = useState('');
  const [days, setDays] = useState(30);
  const [report, setReport] = useState(null);
  const [error, setError] = useState('');
  const [busy, setBusy] = useState(false);

  useEffect(() => { document.title = 'Portfolio analytics | Muhammed Nihas'; }, []);

  useEffect(() => {
    fetch('/api/admin/session', { credentials: 'same-origin' })
      .then(readJson)
      .then((data) => setAuthenticated(data.authenticated))
      .catch((failure) => { setAuthenticated(false); setError(failure.message); });
  }, []);

  useEffect(() => {
    if (!authenticated) return undefined;
    const controller = new AbortController();
    setBusy(true);
    setError('');
    fetch(`/api/admin/analytics?days=${days}`, { credentials: 'same-origin', signal: controller.signal })
      .then(readJson)
      .then(setReport)
      .catch((failure) => {
        if (failure.name === 'AbortError') return;
        if (failure.message === 'Authentication required') setAuthenticated(false);
        setError(failure.message);
      })
      .finally(() => setBusy(false));
    return () => controller.abort();
  }, [authenticated, days]);

  const login = async (event) => {
    event.preventDefault();
    setBusy(true);
    setError('');
    try {
      await readJson(await fetch('/api/admin/login', { method: 'POST', headers: { 'Content-Type': 'application/json' }, credentials: 'same-origin', body: JSON.stringify({ password }) }));
      setPassword('');
      setAuthenticated(true);
    } catch (failure) { setError(failure.message); }
    finally { setBusy(false); }
  };

  const logout = async () => {
    try { await fetch('/api/admin/logout', { method: 'POST', credentials: 'same-origin' }); }
    finally { setAuthenticated(false); setReport(null); }
  };

  if (authenticated === null) return <main className="admin-page"><p>Checking access…</p></main>;
  if (!authenticated) return <main className="admin-page"><div className="admin-login"><a href="/" className="admin-back">← Portfolio</a><span className="section-number">PRIVATE / ANALYTICS</span><h1>Admin access</h1><p>Traffic reports are available only to the portfolio owner.</p><form onSubmit={login}><label htmlFor="admin-password">Password</label><input id="admin-password" type="password" autoComplete="current-password" value={password} onChange={(event) => setPassword(event.target.value)} required /><button className="button button-primary" type="submit" disabled={busy}>{busy ? 'Checking…' : 'Sign in'}</button></form>{error && <p className="admin-error" role="alert">{error}</p>}</div></main>;

  const maxViews = Math.max(1, ...(report?.daily || []).map((row) => row.pageviews || 0));
  return <main className="admin-page"><div className="admin-shell"><header className="admin-header"><div><a href="/" className="admin-back">← Portfolio</a><span className="section-number">PRIVATE / ANALYTICS</span><h1>Portfolio traffic</h1><p>Anonymous, aggregate reports. No visitor identities or individual browsing trails.</p></div><button type="button" className="admin-logout" onClick={logout}>Sign out</button></header>
    <div className="admin-toolbar"><label htmlFor="report-days">Reporting period</label><select id="report-days" value={days} onChange={(event) => setDays(Number(event.target.value))}><option value="7">Last 7 days</option><option value="30">Last 30 days</option><option value="90">Last 90 days</option></select><button type="button" disabled={!report || busy} onClick={() => downloadReport(report, days)}>Export CSV ↓</button></div>
    {error && <p className="admin-error" role="alert">{error}</p>}
    {busy && <p role="status">Loading report…</p>}
    {report && <><div className="admin-kpis"><div><span>PAGE VIEWS</span><strong>{number(report.totals.pageviews)}</strong></div><div><span>ANONYMOUS VISITORS</span><strong>{number(report.totals.visitors)}</strong></div><div><span>REPORTING WINDOW</span><strong>{days} days</strong></div></div>
      <section className="admin-panel admin-trend"><div className="admin-panel-heading"><h2>Daily visit history</h2><p>One row per day, never per person.</p></div>{report.daily.length ? <><div className="admin-bars" role="img" aria-label="Daily page views shown as bars">{report.daily.map((row) => <div key={row.timestamp} title={`${new Date(row.timestamp).toLocaleDateString()}: ${number(row.pageviews)} views`} style={{ height: `${Math.max(4, 100 * (row.pageviews || 0) / maxViews)}%` }} />)}</div><div className="admin-table-scroll"><table><thead><tr><th scope="col">Day</th><th scope="col">Views</th><th scope="col">Visitors</th></tr></thead><tbody>{report.daily.map((row) => <tr key={row.timestamp}><td>{new Date(row.timestamp).toLocaleDateString()}</td><td>{number(row.pageviews)}</td><td>{number(row.visitors)}</td></tr>)}</tbody></table></div></> : <p className="admin-empty">No data in this period.</p>}</section>
      <div className="admin-grid">{REPORT_SECTIONS.map(([key, title, field]) => (key === 'events' || key === 'landings') && !report.eventsAvailable ? <section className="admin-panel" key={key}><h2>{title}</h2><p className="admin-empty">Interaction counts are unavailable. Check the server-side Upstash configuration.</p></section> : <Breakdown key={key} title={title} rows={report[key] || []} field={field} metric={key === 'events' || key === 'landings' ? 'count' : 'pageviews'} />)}</div>
      <p className="admin-footnote">Locations are country-level estimates. “Direct / unknown” includes visits without a referrer. Section views count navigation within this single-page portfolio. Landing sections show consented first-page events only.</p>
    </>}
  </div></main>;
};

export default AdminDashboard;
